import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import axios from 'axios';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { CONFIG_DARK, darkLinear, darkSurfaceCardSx } from './configSurface';

const THEME = '#4ce1b6';
const WA_GREEN = '#25D366';
const EMAIL_ACCENT = '#0d9488';

const DARK = CONFIG_DARK;

const darkMessagingCard = darkSurfaceCardSx;

const darkMessagingHeader = {
  background: darkLinear(`${DARK.elevated} 0%, ${DARK.surface} 100%`),
  borderBottom: `1px solid ${DARK.divider}`,
  boxShadow: `0 1px 0 ${alpha('#ffffff', 0.04)} inset`,
};

const darkRowDivider = DARK.divider;

function masterChannelDarkSx(accent, enabled) {
  if (!enabled) {
    return {
      background: darkLinear(`${DARK.elevated} 0%, ${DARK.surface} 100%`),
      borderColor: DARK.borderStrong,
      boxShadow: 'none',
    };
  }
  return {
    background: darkLinear(
      `${alpha(accent, 0.2)} 0%, ${alpha(accent, 0.07)} 42%, ${DARK.surface} 100%`
    ),
    borderColor: alpha(accent, 0.42),
    boxShadow: `0 0 16px ${alpha(accent, 0.1)}, 0 1px 0 ${alpha('#ffffff', 0.05)} inset`,
  };
}

function toggleDarkSx(accent, active) {
  if (active) {
    return {
      background: darkLinear(
        `${alpha(accent, 0.22)} 0%, ${alpha(accent, 0.08)} 55%, ${DARK.surface} 100%`
      ),
      borderColor: alpha(accent, 0.45),
      boxShadow: `0 0 10px ${alpha(accent, 0.12)}`,
    };
  }
  return {
    background: darkLinear(`${DARK.elevated} 0%, ${DARK.surface} 100%`),
    borderColor: DARK.borderStrong,
    boxShadow: `0 1px 0 ${alpha('#ffffff', 0.04)} inset`,
  };
}

/** Slightly larger than ultra-compact; still dense list layout */
const SZ = {
  iconToggle: 18,
  iconEvent: 22,
  iconMaster: 24,
  fontToggle: '0.8rem',
  fontTitle: '0.9rem',
  fontDesc: '0.8rem',
  fontBadge: '0.72rem',
  fontSection: '0.8rem',
};

const EVENT_META = {
  booking_created: {
    icon: EventAvailableIcon,
    accent: '#0d9488',
    trigger: 'auto',
  },
  booking_cancelled: {
    icon: EventBusyIcon,
    accent: '#dc2626',
    trigger: 'auto',
  },
  review_guest: {
    icon: RateReviewOutlinedIcon,
    accent: '#7c3aed',
    trigger: 'manual',
  },
  customer_offer: {
    icon: LocalOfferOutlinedIcon,
    accent: '#ea580c',
    trigger: 'manual',
  },
  payment_received: {
    icon: PaymentsOutlinedIcon,
    accent: '#2563eb',
    trigger: 'auto',
  },
  web_checkin: {
    icon: HowToRegOutlinedIcon,
    accent: '#059669',
    trigger: 'manual',
  },
  payment_link: {
    icon: LinkOutlinedIcon,
    accent: '#0891b2',
    trigger: 'manual',
  },
  invoice_voucher: {
    icon: ReceiptLongOutlinedIcon,
    accent: '#4f46e5',
    trigger: 'manual',
  },
  hall_event: {
    icon: CelebrationOutlinedIcon,
    accent: '#0f766e',
    trigger: 'auto',
  },
  housekeeping_maintenance: {
    icon: BuildOutlinedIcon,
    accent: '#f59e0b',
    trigger: 'auto',
  },
  housekeeping_lost_found: {
    icon: FindInPageOutlinedIcon,
    accent: '#0ea5e9',
    trigger: 'auto',
  },
};

const EVENT_ORDER = [
  'booking_created',
  'booking_cancelled',
  'payment_received',
  'web_checkin',
  'payment_link',
  'invoice_voucher',
  'hall_event',
  'housekeeping_maintenance',
  'housekeeping_lost_found',
  'review_guest',
  'customer_offer',
];

const EVENT_DEFAULTS = {
  web_checkin: {
    label: 'Web check-in invite',
    description: 'Sent when staff shares a web check-in link with a guest.',
    guest: {
      email: false,
      whatsapp: false,
    },
    hotel: {
      email: false,
    },
  },
  hall_event: {
    label: 'Hall event notification',
    description: 'Guest confirmation/invoice and hotelier email for banquet / hall bookings.',
    auto: {
      label: 'On event create (automatic)',
      description: 'Send when a hall event booking is created.',
      guest: {
        email: false,
        whatsapp: false,
      },
      hotel: {
        email: false,
      },
    },
    manual: {
      label: 'Manual send',
      description: 'Send from the event details documents menu.',
      guest: {
        email: false,
        whatsapp: false,
      },
      hotel: {
        email: false,
      },
    },
  },
  housekeeping_maintenance: {
    label: 'Maintenance request',
    description: 'Alert hotel staff when housekeeping reports a room maintenance issue.',
    hotel: {
      email: false,
      whatsapp: false,
    },
  },
  housekeeping_lost_found: {
    label: 'Lost & found',
    description: 'Alert hotel staff when housekeeping records a found item.',
    hotel: {
      email: false,
      whatsapp: false,
    },
  },
};

function withCommunicationEventDefaults(data) {
  if (!data) return data;
  const events = { ...(data.events || {}) };
  Object.entries(EVENT_DEFAULTS).forEach(([key, fallback]) => {
    if (key === 'hall_event') {
      const current = events[key] || {};
      events[key] = {
        ...fallback,
        ...current,
        auto: {
          ...fallback.auto,
          ...(current.auto || {}),
          guest: {
            ...(fallback.auto?.guest || {}),
            ...(current.auto?.guest || {}),
          },
          hotel: {
            ...(fallback.auto?.hotel || {}),
            ...(current.auto?.hotel || {}),
          },
        },
        manual: {
          ...fallback.manual,
          ...(current.manual || {}),
          guest: {
            ...(fallback.manual?.guest || {}),
            ...(current.manual?.guest || {}),
          },
          hotel: {
            ...(fallback.manual?.hotel || {}),
            ...(current.manual?.hotel || {}),
          },
        },
      };
      return;
    }
    events[key] = {
      ...fallback,
      ...(events[key] || {}),
      guest: {
        ...(fallback.guest || {}),
        ...(events[key]?.guest || {}),
      },
      hotel: {
        ...(fallback.hotel || {}),
        ...(events[key]?.hotel || {}),
      },
    };
  });
  return { ...data, events };
}

const REPORT_CADENCES = ['daily', 'weekly', 'monthly'];

const REPORT_CADENCE_META = {
  daily: {
    icon: TodayOutlinedIcon,
    accent: '#0284c7',
    label: 'Daily',
  },
  weekly: {
    icon: ViewWeekOutlinedIcon,
    accent: '#7c3aed',
    label: 'Weekly',
  },
  monthly: {
    icon: CalendarMonthOutlinedIcon,
    accent: '#c2410c',
    label: 'Monthly',
  },
};

const REPORT_SCHEDULE_HINT = {
  daily: 'Runs every day when scheduled (yesterday’s metrics).',
  weekly: 'Runs on Mondays (last 7 days).',
  monthly: 'Runs on the 1st (previous calendar month).',
};

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const api = axios.create({
  withCredentials: true,
  headers: { 'X-CSRFToken': getCookie('csrftoken') },
});

function CompactToggle({ text, icon: Icon, accent, active, disabled, onChange, isDark }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.25}
      onClick={() => !disabled && onChange(!active)}
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: 1.25,
        border: '1px solid',
        ...(isDark
          ? toggleDarkSx(accent, active && !disabled)
          : {
              borderColor: active ? alpha(accent, 0.45) : 'divider',
              bgcolor: active ? alpha(accent, 0.12) : 'transparent',
            }),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        flexShrink: 0,
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <Icon sx={{ fontSize: SZ.iconToggle, color: active ? accent : 'text.disabled' }} />
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: SZ.fontToggle, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {text}
      </Typography>
      <Switch
        size="small"
        checked={active}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          p: 0.35,
          '& .MuiSwitch-thumb': { width: 16, height: 16 },
          '& .MuiSwitch-switchBase': { p: '4px' },
          '& .Mui-checked': { color: accent },
          '& .Mui-checked + .MuiSwitch-track': { backgroundColor: `${accent} !important` },
        }}
      />
    </Stack>
  );
}

function MasterChannelRow({ title, icon: Icon, enabled, onToggle, accent, isDark, disabled = false, tooltip = '' }) {
  const switchControl = (
    <Switch
      checked={enabled}
      disabled={disabled}
      onChange={(e) => onToggle(e.target.checked)}
      sx={{
        '& .Mui-checked': { color: accent },
        '& .Mui-checked + .MuiSwitch-track': { backgroundColor: accent },
      }}
    />
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderLeft: `3px solid ${accent}`,
        ...(isDark
          ? masterChannelDarkSx(accent, enabled)
          : {
              borderColor: enabled ? alpha(accent, 0.5) : '#e2e8f0',
              bgcolor: '#fff',
            }),
        transition: 'background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <Icon sx={{ fontSize: SZ.iconMaster, color: accent }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle }}>
          {title}
        </Typography>
      </Stack>
      {disabled && tooltip ? (
        <Tooltip title={tooltip} arrow>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {switchControl}
          </Box>
        </Tooltip>
      ) : switchControl}
    </Stack>
  );
}

function countSectionToggles(section, channelMaster) {
  if (!section) return 0;
  const g = section.guest || {};
  const h = section.hotel || {};
  return [
    g.email && channelMaster.email,
    g.whatsapp && channelMaster.whatsapp,
    h.email && channelMaster.email,
    h.whatsapp && channelMaster.whatsapp,
  ].filter(Boolean).length;
}

function TriggerBadge({ trigger, isDark }) {
  const isAuto = trigger === 'auto';
  return (
    <Chip
      size="small"
      label={isAuto ? 'Auto' : 'Manual'}
      sx={{
        height: 22,
        fontSize: SZ.fontBadge,
        fontWeight: 700,
        '& .MuiChip-label': { px: 1 },
        ...(isDark
          ? {
              background: isAuto
                ? darkLinear(`${alpha('#34d399', 0.22)} 0%, ${alpha('#059669', 0.12)} 100%`)
                : darkLinear(`${alpha('#fbbf24', 0.2)} 0%, ${alpha('#d97706', 0.1)} 100%`),
              color: isAuto ? '#6ee7b7' : '#fcd34d',
              border: `1px solid ${alpha(isAuto ? '#34d399' : '#fbbf24', 0.25)}`,
            }
          : {
              bgcolor: isAuto ? alpha(THEME, 0.12) : alpha('#f59e0b', 0.12),
              color: isAuto ? '#0f766e' : '#b45309',
            }),
      }}
    />
  );
}

function ToggleGroup({ items }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap justifyContent="flex-end">
      {items}
    </Stack>
  );
}

function buildGuestToggles(guest, hotel, channelMaster, onGuest, onHotel, isDark) {
  const items = [];
  if (guest?.email !== undefined) {
    items.push(
      <CompactToggle
        key="g-email"
        text="Guest Email"
        icon={EmailOutlinedIcon}
        accent={THEME}
        active={Boolean(guest.email) && channelMaster.email}
        disabled={!channelMaster.email}
        onChange={(v) => onGuest('email', v)}
        isDark={isDark}
      />
    );
  }
  if (guest?.whatsapp !== undefined) {
    items.push(
      <CompactToggle
        key="g-wa"
        text="Guest WA"
        icon={WhatsAppIcon}
        accent={WA_GREEN}
        active={Boolean(guest.whatsapp) && channelMaster.whatsapp}
        disabled={!channelMaster.whatsapp}
        onChange={(v) => onGuest('whatsapp', v)}
        isDark={isDark}
      />
    );
  }
  if (hotel?.email !== undefined) {
    items.push(
      <CompactToggle
        key="h-email"
        text="Hotel Email"
        icon={BusinessOutlinedIcon}
        accent="#64748b"
        active={Boolean(hotel.email) && channelMaster.email}
        disabled={!channelMaster.email}
        onChange={(v) => onHotel('email', v)}
        isDark={isDark}
      />
    );
  }
  if (hotel?.whatsapp !== undefined) {
    items.push(
      <CompactToggle
        key="h-wa"
        text="Hotel WA"
        icon={WhatsAppIcon}
        accent={WA_GREEN}
        active={Boolean(hotel.whatsapp) && channelMaster.whatsapp}
        disabled={!channelMaster.whatsapp}
        onChange={(v) => onHotel('whatsapp', v)}
        isDark={isDark}
      />
    );
  }
  return items;
}

function EventRow({ eventKey, eventCfg, channelMaster, onToggle, isDark, isLast }) {
  if (!eventCfg) return null;
  const meta = EVENT_META[eventKey] || { icon: EmailOutlinedIcon, accent: THEME, trigger: 'manual' };
  const EventIcon = meta.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: { xs: 0.75, md: 1 },
        py: 1,
        px: { xs: 0.75, md: 1.25 },
        borderBottom: isLast ? 'none' : `1px solid ${isDark ? darkRowDivider : '#e2e8f0'}`,
        ...(isDark && {
          transition: 'background 0.2s ease',
          '&:hover': {
            background: darkLinear(
              `${alpha('#ffffff', 0.03)} 0%, transparent 55%, ${alpha('#ffffff', 0.015)} 100%`
            ),
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
        <EventIcon sx={{ fontSize: SZ.iconEvent, color: meta.accent, mt: 0.2, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle, lineHeight: 1.3 }}>
              {eventCfg.label}
            </Typography>
            <TriggerBadge trigger={meta.trigger} isDark={isDark} />
          </Stack>
          {eventCfg.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: SZ.fontDesc, lineHeight: 1.35, mt: 0.25 }}
            >
              {eventCfg.description}
            </Typography>
          )}
        </Box>
      </Stack>
      <ToggleGroup
        items={buildGuestToggles(
          eventCfg.guest,
          eventCfg.hotel,
          channelMaster,
          (ch, v) => onToggle(eventKey, 'guest', ch, v),
          (ch, v) => onToggle(eventKey, 'hotel', ch, v),
          isDark
        )}
      />
    </Box>
  );
}

function HotelReportsSection({
  reportsCfg,
  channelMaster,
  onReportToggle,
  onSendNow,
  onDownloadReport,
  isDark,
  sendingCadence,
  downloadingCadence,
}) {
  if (!reportsCfg) return null;

  return (
    <Box sx={{ px: 0.5, py: 0.25 }}>
      {REPORT_CADENCES.map((cadence, idx) => {
        const section = reportsCfg[cadence];
        if (!section) return null;
        const hotel = section.hotel || {};
        const meta = REPORT_CADENCE_META[cadence] || { icon: TodayOutlinedIcon, accent: THEME, label: cadence };
        const CadenceIcon = meta.icon;
        const isLast = idx === REPORT_CADENCES.length - 1;
        return (
          <Box
            key={cadence}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              gap: { xs: 0.75, md: 1 },
              py: 1,
              px: { xs: 0.75, md: 1.25 },
              borderBottom: isLast ? 'none' : `1px solid ${isDark ? darkRowDivider : '#e2e8f0'}`,
              ...(isDark && {
                transition: 'background 0.2s ease',
                '&:hover': {
                  background: darkLinear(
                    `${alpha('#ffffff', 0.03)} 0%, transparent 55%, ${alpha('#ffffff', 0.015)} 100%`
                  ),
                },
              }),
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
              <CadenceIcon sx={{ fontSize: SZ.iconEvent, color: meta.accent, mt: 0.2, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle, lineHeight: 1.3 }}>
                  {meta.label || section.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: SZ.fontDesc, lineHeight: 1.35, mt: 0.25 }}
                >
                  {REPORT_SCHEDULE_HINT[cadence]}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.75}
              alignItems="center"
              useFlexGap
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              sx={{ pl: { xs: 4.25, md: 0 } }}
            >
              <ToggleGroup
                items={[
                  hotel.email !== undefined && (
                    <CompactToggle
                      key="h-email"
                      text="Hotel Email"
                      icon={EmailOutlinedIcon}
                      accent={EMAIL_ACCENT}
                      active={Boolean(hotel.email) && channelMaster.email}
                      disabled={!channelMaster.email}
                      onChange={(v) => onReportToggle(cadence, 'email', v)}
                      isDark={isDark}
                    />
                  ),
                  hotel.whatsapp !== undefined && (
                    <CompactToggle
                      key="h-wa"
                      text="Hotel WA"
                      icon={WhatsAppIcon}
                      accent={WA_GREEN}
                      active={Boolean(hotel.whatsapp) && channelMaster.whatsapp}
                      disabled={!channelMaster.whatsapp}
                      onChange={(v) => onReportToggle(cadence, 'whatsapp', v)}
                      isDark={isDark}
                    />
                  ),
                ].filter(Boolean)}
              />
              <Chip
                size="small"
                icon={
                  sendingCadence === cadence ? (
                    <CircularProgress size={12} sx={{ color: `${THEME} !important` }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 14 }} />
                  )
                }
                label="Send now"
                clickable
                disabled={sendingCadence != null}
                onClick={() => onSendNow(cadence)}
                sx={{ fontWeight: 600, fontSize: SZ.fontBadge }}
              />
              <Chip
                size="small"
                icon={
                  downloadingCadence === cadence ? (
                    <CircularProgress size={12} sx={{ color: `${THEME} !important` }} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
                  )
                }
                label="Download PDF"
                clickable
                disabled={downloadingCadence != null}
                onClick={() => onDownloadReport(cadence)}
                sx={{ fontWeight: 600, fontSize: SZ.fontBadge }}
              />
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function ReviewGuestRow({ eventCfg, channelMaster, onReviewToggle, isDark }) {
  if (!eventCfg) return null;
  const meta = EVENT_META.review_guest;
  const manual = eventCfg.manual || {};
  const checkoutAuto = eventCfg.checkout_auto || {};

  const sectionBlock = (sectionKey, section, trigger) => {
    const g = section?.guest || {};
    return (
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
          <TriggerBadge trigger={trigger} isDark={isDark} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: SZ.fontSection }}>
            {section?.label}
          </Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
          {g.email !== undefined && (
            <CompactToggle
              text="Email"
              icon={EmailOutlinedIcon}
              accent={THEME}
              active={Boolean(g.email) && channelMaster.email}
              disabled={!channelMaster.email}
              onChange={(v) => onReviewToggle(sectionKey, 'email', v)}
              isDark={isDark}
            />
          )}
          {g.whatsapp !== undefined && (
            <CompactToggle
              text="WhatsApp"
              icon={WhatsAppIcon}
              accent={WA_GREEN}
              active={Boolean(g.whatsapp) && channelMaster.whatsapp}
              disabled={!channelMaster.whatsapp}
              onChange={(v) => onReviewToggle(sectionKey, 'whatsapp', v)}
              isDark={isDark}
            />
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        py: 1,
        px: { xs: 0.75, md: 1.25 },
        borderTop: `1px solid ${isDark ? darkRowDivider : '#e2e8f0'}`,
        ...(isDark && {
          background: darkLinear('transparent 0%, rgba(255,255,255,0.015) 50%, transparent 100%'),
        }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <meta.icon sx={{ fontSize: SZ.iconEvent, color: meta.accent }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle }}>
          {eventCfg.label}
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {sectionBlock('manual', manual, 'manual')}
        {sectionBlock('checkout_auto', checkoutAuto, 'auto')}
      </Stack>
    </Box>
  );
}

function HallEventRow({ eventCfg, channelMaster, onHallEventToggle, isDark }) {
  if (!eventCfg) return null;
  const meta = EVENT_META.hall_event;
  const auto = eventCfg.auto || {};
  const manual = eventCfg.manual || {};

  const sectionRow = (sectionKey, section, trigger) => {
    const g = section?.guest || {};
    const h = section?.hotel || {};
    const toggles = buildGuestToggles(
      g,
      h,
      channelMaster,
      (channel, value) => onHallEventToggle(sectionKey, 'guest', channel, value),
      (channel, value) => onHallEventToggle(sectionKey, 'hotel', channel, value),
      isDark,
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          gap: { xs: 0.75, md: 1 },
          py: 0.85,
          px: { xs: 0.25, md: 0.5 },
          borderTop: `1px solid ${isDark ? darkRowDivider : '#e2e8f0'}`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <TriggerBadge trigger={trigger} isDark={isDark} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle, lineHeight: 1.3 }}>
              {section?.label}
            </Typography>
            {section?.description ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  color: isDark ? DARK.textMuted : 'text.secondary',
                  fontSize: SZ.fontDesc,
                  lineHeight: 1.35,
                }}
              >
                {section.description}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <ToggleGroup items={toggles} />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        py: 1,
        px: { xs: 0.75, md: 1.25 },
        borderTop: `1px solid ${isDark ? darkRowDivider : '#e2e8f0'}`,
        ...(isDark && {
          background: darkLinear('transparent 0%, rgba(255,255,255,0.015) 50%, transparent 100%'),
        }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 0.35 }}>
        <meta.icon sx={{ fontSize: SZ.iconEvent, color: meta.accent, mt: 0.2, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: SZ.fontTitle, lineHeight: 1.3 }}>
            {eventCfg.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isDark ? DARK.textMuted : 'text.secondary', fontSize: SZ.fontDesc }}
          >
            {eventCfg.description}
          </Typography>
        </Box>
      </Stack>
      <Stack spacing={0} sx={{ mt: 0.5 }}>
        {sectionRow('auto', auto, 'auto')}
        {sectionRow('manual', manual, 'manual')}
      </Stack>
    </Box>
  );
}

const SAVE_DEBOUNCE_MS = 400;

const CommunicationSettings = () => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [sendReportMsg, setSendReportMsg] = useState('');
  const [sendingCadence, setSendingCadence] = useState(null);
  const [downloadingCadence, setDownloadingCadence] = useState(null);
  const hydratedRef = useRef(false);
  const lastSavedJsonRef = useRef(null);
  const saveTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    hydratedRef.current = false;
    try {
      const res = await api.get('/configuration/api/communication-settings/');
      const normalized = withCommunicationEventDefaults(res.data);
      setSettings(normalized);
      lastSavedJsonRef.current = JSON.stringify(normalized);
      hydratedRef.current = true;
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return watchThemeChanges(setCurrentTheme);
  }, [load]);

  useEffect(() => {
    if (!settings || loading || !hydratedRef.current) return;

    const snapshot = JSON.stringify(settings);
    if (snapshot === lastSavedJsonRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaveState('saving');
      setError('');
      try {
        const res = await api.patch('/configuration/api/communication-settings/', settings);
        const normalized = withCommunicationEventDefaults(res.data);
        setSettings(normalized);
        lastSavedJsonRef.current = JSON.stringify(normalized);
        setSaveState('saved');
      } catch (e) {
        setSaveState('error');
        setError(e?.response?.data?.error || e?.message || 'Failed to save settings');
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [settings, loading]);

  useEffect(() => {
    if (saveState !== 'saved') return undefined;
    const t = setTimeout(() => setSaveState('idle'), 2500);
    return () => clearTimeout(t);
  }, [saveState]);

  const handleMasterToggle = (channel, value) => {
    setSettings((prev) => (prev ? { ...prev, [`${channel}_enabled`]: value } : prev));
  };

  const handleEventToggle = (eventKey, audience, channel, value) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const events = { ...(prev.events || {}) };
      const eventCfg = events[eventKey] || EVENT_DEFAULTS[eventKey];
      if (!eventCfg) return prev;
      const next = { ...prev, events };
      next.events[eventKey] = {
        ...eventCfg,
        [audience]: {
          ...(eventCfg[audience] || {}),
          [channel]: value,
        },
      };
      return next;
    });
  };

  const handleReportToggle = (cadence, channel, value) => {
    setSettings((prev) => {
      if (!prev?.events?.hotel_reports) return prev;
      const reports = prev.events.hotel_reports;
      const section = reports[cadence] || { hotel: {} };
      return {
        ...prev,
        events: {
          ...prev.events,
          hotel_reports: {
            ...reports,
            [cadence]: {
              ...section,
              hotel: {
                ...section.hotel,
                [channel]: value,
              },
            },
          },
        },
      };
    });
  };

  const handleSendReportNow = async (cadence) => {
    const section = settings?.events?.hotel_reports?.[cadence]?.hotel || {};
    const send_email = Boolean(section.email) && channelMaster.email;
    const send_whatsapp = Boolean(section.whatsapp) && channelMaster.whatsapp;
    if (!send_email && !send_whatsapp) {
      setSendReportMsg('Enable Hotel Email or Hotel WA for this cadence first.');
      return;
    }
    setSendingCadence(cadence);
    setSendReportMsg('');
    try {
      const res = await api.post('/configuration/api/communication-settings/send-report/', {
        cadence,
        send_email,
        send_whatsapp,
        force_schedule: true,
      });
      const parts = [];
      if (res.data.email_sent) parts.push('email sent');
      if (res.data.whatsapp_sent) parts.push('WhatsApp sent');
      if (res.data.period_label) parts.unshift(res.data.period_label);
      if (res.data.errors?.length) parts.push(res.data.errors.join('; '));
      setSendReportMsg(parts.join(' · ') || 'Report requested.');
    } catch (e) {
      setSendReportMsg(e?.response?.data?.error || e?.message || 'Failed to send report');
    } finally {
      setSendingCadence(null);
    }
  };

  const handleDownloadReportPdf = async (cadence) => {
    setDownloadingCadence(cadence);
    setSendReportMsg('');
    try {
      const res = await api.get('/configuration/api/communication-settings/send-report/', {
        params: { cadence },
        responseType: 'blob',
      });
      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `${cadence}-performance-report.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSendReportMsg(`${REPORT_CADENCE_META[cadence]?.label || cadence} PDF downloaded.`);
    } catch (e) {
      setSendReportMsg(e?.response?.data?.error || e?.message || 'Failed to download report PDF');
    } finally {
      setDownloadingCadence(null);
    }
  };

  const handleReviewToggle = (sectionKey, channel, value) => {
    setSettings((prev) => {
      if (!prev?.events?.review_guest) return prev;
      const review = prev.events.review_guest;
      const section = review[sectionKey] || { guest: {} };
      return {
        ...prev,
        events: {
          ...prev.events,
          review_guest: {
            ...review,
            [sectionKey]: {
              ...section,
              guest: {
                ...section.guest,
                [channel]: value,
              },
            },
          },
        },
      };
    });
  };

  const handleHallEventToggle = (sectionKey, audience, channel, value) => {
    setSettings((prev) => {
      if (!prev?.events?.hall_event) return prev;
      const hallEvent = prev.events.hall_event;
      const section = hallEvent[sectionKey] || { guest: {}, hotel: {} };
      return {
        ...prev,
        events: {
          ...prev.events,
          hall_event: {
            ...hallEvent,
            [sectionKey]: {
              ...section,
              [audience]: {
                ...(section[audience] || {}),
                [channel]: value,
              },
            },
          },
        },
      };
    });
  };

  const channelMaster = useMemo(
    () =>
      settings
        ? { whatsapp: settings.whatsapp_enabled, email: settings.email_enabled }
        : { whatsapp: false, email: false },
    [settings]
  );
  const whatsappAvailable = settings?.whatsapp_available !== false;
  const whatsappUnavailableReason =
    settings?.whatsapp_unavailable_reason ||
    'WhatsApp communication is included in Pro and Ultra. Core and Flex hotels can enable it by purchasing the WhatsApp add-on.';

  const reportsEnabledCount = useMemo(() => {
    if (!settings?.events?.hotel_reports) return 0;
    let count = 0;
    REPORT_CADENCES.forEach((cadence) => {
      const h = settings.events.hotel_reports[cadence]?.hotel || {};
      if ((h.email && channelMaster.email) || (h.whatsapp && channelMaster.whatsapp)) {
        count += 1;
      }
    });
    return count;
  }, [settings, channelMaster]);

  const enabledEventsCount = useMemo(() => {
    if (!settings?.events) return 0;
    let count = 0;

    EVENT_ORDER.forEach((key) => {
      const ev = settings.events[key];
      if (!ev) return;
      if (key === 'review_guest') {
        count += countSectionToggles(ev.manual, channelMaster);
        count += countSectionToggles(ev.checkout_auto, channelMaster);
        return;
      }
      if (key === 'hall_event') {
        count += countSectionToggles(ev.auto, channelMaster);
        count += countSectionToggles(ev.manual, channelMaster);
        return;
      }
      const g = ev.guest || {};
      const h = ev.hotel || {};
      if (
        (g.email && channelMaster.email) ||
        (g.whatsapp && channelMaster.whatsapp) ||
        (h.email && channelMaster.email) ||
        (h.whatsapp && channelMaster.whatsapp)
      ) {
        count += 1;
      }
    });
    return count;
  }, [settings, channelMaster]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: THEME }} size={44} />
        <Typography variant="body2" color="text.secondary">
          Loading communication preferences…
        </Typography>
      </Box>
    );
  }

  if (!settings) {
    return <Alert severity="error">{error || 'Unable to load communication settings.'}</Alert>;
  }

  const saveStatusChip = (() => {
    if (saveState === 'saving') {
      return (
        <Chip
          size="small"
          icon={<CircularProgress size={14} sx={{ color: `${THEME} !important` }} />}
          label="Saving…"
          sx={{ fontWeight: 700, bgcolor: alpha(THEME, 0.12) }}
        />
      );
    }
    if (saveState === 'saved') {
      return (
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
          label="Saved"
          sx={{ fontWeight: 700, bgcolor: alpha(THEME, 0.2), color: isDark ? THEME : '#0f766e' }}
        />
      );
    }
    if (saveState === 'error') {
      return (
        <Chip
          size="small"
          label="Save failed"
          color="error"
          sx={{ fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<CloudSyncIcon sx={{ fontSize: 16 }} />}
        label="Auto-save on"
        variant="outlined"
        sx={{ fontWeight: 600, borderColor: alpha(THEME, 0.35) }}
      />
    );
  })();

  const standardEvents = EVENT_ORDER.filter((k) => k !== 'review_guest' && k !== 'hall_event');
  const reportsCfg = settings.events?.hotel_reports;
  const paperSx = {
    borderRadius: 2,
    overflow: 'hidden',
    ...(isDark
      ? darkMessagingCard
      : {
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
        }),
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ ...paperSx, mb: 2 }}>
        <Box
          sx={{
            px: 1.25,
            py: 1,
            ...(isDark ? darkMessagingHeader : { bgcolor: alpha('#f8fafc', 1) }),
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <MasterChannelRow
                title="WhatsApp"
                icon={WhatsAppIcon}
                enabled={settings.whatsapp_enabled && whatsappAvailable}
                onToggle={(v) => handleMasterToggle('whatsapp', v)}
                accent={WA_GREEN}
                isDark={isDark}
                disabled={!whatsappAvailable}
                tooltip={whatsappUnavailableReason}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MasterChannelRow
                title="Email"
                icon={EmailOutlinedIcon}
                enabled={settings.email_enabled}
                onToggle={(v) => handleMasterToggle('email', v)}
                accent={EMAIL_ACCENT}
                isDark={isDark}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Operations reports — separate block above guest messaging */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
          <AssessmentOutlinedIcon sx={{ fontSize: 28, color: '#0369a1', mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1rem' }}>
              {reportsCfg?.label || 'Operations reports for hoteliers'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.85rem', color: isDark ? DARK.textMuted : 'text.secondary' }}
            >
              {reportsCfg?.description ||
                'Scheduled summaries to hotel email and property phone (WhatsApp).'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: isDark ? DARK.textMuted : 'text.secondary' }}
            >
              Uses master Email / WhatsApp switches below · auto-save on toggle
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          {saveStatusChip}
          <Chip
            size="small"
            label={`${reportsEnabledCount} report${reportsEnabledCount === 1 ? '' : 's'} on`}
            sx={{
              height: 26,
              fontSize: '0.75rem',
              fontWeight: 700,
              ...(isDark && {
                background: darkLinear(`${DARK.elevated} 0%, ${DARK.surface} 100%`),
                border: `1px solid ${DARK.borderStrong}`,
                color: '#e5e7eb',
              }),
            }}
          />
        </Stack>
      </Stack>

      {sendReportMsg && (
        <Alert
          severity={sendReportMsg.toLowerCase().includes('fail') ? 'warning' : 'info'}
          sx={{ mb: 1.5, py: 0.5 }}
          onClose={() => setSendReportMsg('')}
        >
          {sendReportMsg}
        </Alert>
      )}

      <Paper elevation={0} sx={paperSx}>
        <HotelReportsSection
          reportsCfg={reportsCfg}
          channelMaster={channelMaster}
          onReportToggle={handleReportToggle}
          onSendNow={handleSendReportNow}
          onDownloadReport={handleDownloadReportPdf}
          isDark={isDark}
          sendingCadence={sendingCadence}
          downloadingCadence={downloadingCadence}
        />
      </Paper>

      <Divider sx={{ my: 3, borderColor: isDark ? DARK.divider : '#e2e8f0' }} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
          <ForumOutlinedIcon sx={{ fontSize: 28, color: THEME, mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1rem' }}>
              Guest &amp; hotel messaging
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.85rem', color: isDark ? DARK.textMuted : 'text.secondary' }}
            >
              Auto-save on toggle · disabled channels hide send actions
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            size="small"
            label={`${enabledEventsCount} on`}
            sx={{
              height: 26,
              fontSize: '0.75rem',
              fontWeight: 700,
              ...(isDark && {
                background: darkLinear(`${DARK.elevated} 0%, ${DARK.surface} 100%`),
                border: `1px solid ${DARK.borderStrong}`,
                color: '#e5e7eb',
              }),
            }}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ px: 0.5, py: 0.25 }}>
          {standardEvents.map((key) => (
            <EventRow
              key={key}
              eventKey={key}
              eventCfg={settings.events?.[key]}
              channelMaster={channelMaster}
              onToggle={handleEventToggle}
              isDark={isDark}
              isLast={false}
            />
          ))}
          <HallEventRow
            eventCfg={settings.events?.hall_event}
            channelMaster={channelMaster}
            onHallEventToggle={handleHallEventToggle}
            isDark={isDark}
          />
          <ReviewGuestRow
            eventCfg={settings.events?.review_guest}
            channelMaster={channelMaster}
            onReviewToggle={handleReviewToggle}
            isDark={isDark}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CommunicationSettings;
